// ABOUTME: Every in-app route in one place: top-level pages, settings and account tabs, auth screens.
// ABOUTME: Feature code navigates with these names; the strings themselves appear only here and in AppRoutes.

// Top-level pages (AppRoutes mounts them; the sidebar links to them)
export const CALENDAR_PAGE = "/"
export const TRAININGS_PAGE = "/trainings"
export const PLANS_PAGE = "/plans"
export const RATIONS_PAGE = "/rations"
export const ACCOUNT_PAGE = "/account"
export const SETTINGS_PAGE = "/settings"
export const STATISTICS_PAGE = "/statistics"
export const SHOP_PAGE = "/shop"
export const ONBOARDING_PAGE = "/onboarding"
export const NOT_FOUND_PAGE = "/404"

/** Handled before React mounts (src/index.tsx): clears the session and leaves. */
export const LOGOUT_PAGE = "/logout"

// Auth screens live under one prefix; model/navigation decides when to send a user there.
export const AUTH_PATH_PREFIX = "/auth"
export const LOGIN_PAGE = `${AUTH_PATH_PREFIX}/login`

/** PRO landing, one per audience. */
export const proLandingPath = (role: "athlete" | "trainer") => `/pro/${role}`

/** Settings tabs — the slug is the URL segment after /settings/. */
export const SETTINGS_TAB_SLUGS = ["profile", "settings", "coach", "payment"] as const
export type SettingsTabSlug = (typeof SETTINGS_TAB_SLUGS)[number]
export const SETTINGS_ROUTES: Readonly<Record<SettingsTabSlug, string>> = {
  profile: `${SETTINGS_PAGE}/profile`,
  settings: `${SETTINGS_PAGE}/settings`,
  coach: `${SETTINGS_PAGE}/coach`,
  payment: `${SETTINGS_PAGE}/payment`,
}

/** Subscription/payment page — where every PRO upsell sends the user. */
export const SUBSCRIPTION_PATH = SETTINGS_ROUTES.payment

/**
 * Where a stuck user goes for help. The Telegram bot rather than email: it needs no
 * account and no mail client. Same address the auth app uses (`kratos-auth-ui` config).
 */
export const SUPPORT_URL = "https://t.me/TrainingEnduranceBot"

/**
 * Enum for Account/Settings page tab indexes
 * Use this instead of magic numbers when checking or setting active tab
 */
export enum AccountTab {
  Profile = 0,
  Physiology = 1,
  SportTarget = 2,
  TrainingTime = 3,
  Metabolic = 4,
  Comment = 5,
  CoachComment = 6,
}

/** @deprecated Use AccountTab.Metabolic instead */
export const METABOLIC_PROFILE_TAB_INDEX = AccountTab.Metabolic

export const METABOLIC_PROFILE_ANCHOR_ID = "metabolic-intensity-zones"
export const METABOLIC_PROFILE_HASH = `#${METABOLIC_PROFILE_ANCHOR_ID}`
export const ACCOUNT_METABOLIC_PROFILE_PATH = `${ACCOUNT_PAGE}/metabolic${METABOLIC_PROFILE_HASH}`

/** Route slugs for account tabs, indexed by AccountTab enum */
export const ACCOUNT_TAB_SLUGS = [
  "general", // AccountTab.Profile
  "physiology", // AccountTab.Physiology
  "target", // AccountTab.SportTarget
  "training", // AccountTab.TrainingTime
  "metabolic", // AccountTab.Metabolic
  "comment", // AccountTab.Comment
  "coach-comment", // AccountTab.CoachComment
] as const

export type AccountTabSlug = (typeof ACCOUNT_TAB_SLUGS)[number]

// Account tab routes
export const ACCOUNT_TAB_ROUTES = {
  general: "/account/general",
  physiology: "/account/physiology",
  target: "/account/target",
  training: "/account/training",
  metabolic: "/account/metabolic",
  comment: "/account/comment",
  coachComment: "/account/coach-comment",
} as const

/** Anchor of the "nutrition goal" card on the sport-target tab. */
export const NUTRITION_TARGET_ANCHOR_ID = "nutrition-target"
export const NUTRITION_TARGET_HASH = `#${NUTRITION_TARGET_ANCHOR_ID}`

/**
 * Where the goal shown on a day's meal plan is actually set. A coach reads an
 * athlete's day, so the athlete travels along in the query — same shape the
 * metabolic-profile links use.
 */
export const accountNutritionTargetPath = (athleteID?: string) =>
  athleteID
    ? `${ACCOUNT_TAB_ROUTES.target}?athleteID=${athleteID}${NUTRITION_TARGET_HASH}`
    : `${ACCOUNT_TAB_ROUTES.target}${NUTRITION_TARGET_HASH}`
